/* eslint-disable @typescript-eslint/no-explicit-any */
import { CreateSimulationGroupRequest } from '@thive/fddm-types';
import { create } from 'zustand';

// DeepPartial type (recursive)
export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

// Simple deep Merge
function deepMerge<T>(target: T, source: DeepPartial<T>): T {
  const output = { ...target };

  Object.keys(source).forEach((key) => {
    const srcVal = source[key as keyof T];
    const tgtVal = output[key as keyof T];

    if (Array.isArray(srcVal)) {
      output[key as keyof T] = [...srcVal] as any;
      return;
    }

    if (srcVal && typeof srcVal === 'object') {
      output[key as keyof T] = deepMerge((tgtVal ?? {}) as any, srcVal as any);
    } else {
      output[key as keyof T] = srcVal as any;
    }
  });

  return output;
}

interface SimulationGroupStore {
  data: CreateSimulationGroupRequest;

  getDraft: () => CreateSimulationGroupRequest;
  setDraft: (value: CreateSimulationGroupRequest) => void;

  updateDraft: (value: DeepPartial<CreateSimulationGroupRequest>) => void;
}

export const useSimulationGroupStore = create<SimulationGroupStore>((set, get) => ({
  data: {
    name: 'DRAFT',
    floorPlanRevision: {
      // TODO: Filled with placeholders
      metaData: {
        projectId: 'temp-project-id',
        alternativeId: 'temp-alternative-id',
        revisionId: 'temp-revision-id',
        projectName: 'Temporary Project',
        alternativeName: 'Temporary Alternative',
        version: 1,
        state: 'DRAFT',
        created: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: 'temp-user',
      },
      uxonly: {
        catalog: { trafficRules: { maxSpeeds: [], vehicleModes: [] }, fileReferences: [] },
        preferences: {},
        groups: [],
        referenceImage: undefined,
        thumbnail: '',
      },
      setup: {
        loadTypes: [],
        vehicleTypes: [],
      },
      design: {
        areas: {
          highwayAreas: [],
          rackingAreas: [],
          singularStationAreas: [],
          stationaryAreas: [],
          manualAreas: [],
        },
        interAreaConnections: [],
        obstacles: [],
        logicalSpaces: [],
        templates: {
          lassieMinis: [],
          lassies: [],
          shuttleRacks: [],
        },
        justInTimeRoutingConfigs: [],
        flows: { flows: [], flowStops: [], dependentFlows: [] },
      },
      floorplan: {
        stations: [],
        vehicleLayouts: [],
      },
      tracks: [],
    },
    config: {
      duration: 0,
      fmsConfig: {
        name: 'FMS-Config',
        distributionStrategy: 0.7,
        failOnNoRouteFound: false,
        manualIntervention: true,
        obstructionTimeOutInSeconds: 30,
        hopsToBlockBeforeClaimedEndpoint: 3,
      },
      vehicleRanges: [],
      uvtOverrides: {
        loadHandlingDurationPerc: 100,
        maxForwardSpeedPerc: 100,
        maxReverseSpeedPerc: 100,
        cstIgnoreObstacles: false,
      },
    },
  },

  getDraft: () => get().data,

  setDraft: (value) => set({ data: value }),

  updateDraft: (value: DeepPartial<CreateSimulationGroupRequest>) =>
    set((state) => ({
      data: deepMerge(state.data, value),
    })),
}));
